import { useQuery } from '@tanstack/react-query';

import { mockList } from '@/lib/mock/db';
import { queryKeys } from '@/lib/queryKeys';
import { noticeCategoryLabel } from '@/lib/types';
import { relativeDay } from '@/pages/home/widgets/format';
import { WidgetFrame, WidgetRow } from '@/pages/home/widgets/WidgetFrame';

/**
 * 공지사항. 숨길 수 없는 고정 위젯이다.
 *
 * 전사에 알려야 하는 내용이 "내가 그 위젯을 켰는지"에 달려 있으면 공지가 아니다.
 * 그래서 위젯 관리에서 끌 수 없다.
 *
 * **보미소식(경조사·생일)은 뺀다** (사용자 지적 2026-08-28). 자기 위젯이 생긴 뒤로 같은 글이
 * 홈에 두 번 서고 있었고, 다섯 줄뿐인 이 칸을 생일 안내가 차지하면 정작 계약·착공 공지가 밀린다.
 * 거르는 곳이 화면인 이유: 저 글들도 **공지사항 게시판의 글**이라 목록 API 는 그대로 내려 줘야
 * 하고(전체 탭에서 보인다), 나뉘는 것은 홈의 두 칸이 무엇을 맡는가일 뿐이다.
 */

const VISIBLE_COUNT = 5;

export function NoticesWidget() {
  const notices = useQuery({
    queryKey: queryKeys.noticePosts,
    queryFn: () => mockList('noticePosts'),
    select: (posts) =>
      posts
        .filter((post) => post.category !== 'bomi')
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .slice(0, VISIBLE_COUNT),
  });

  return (
    <WidgetFrame
      title="공지사항"
      icon="bell"
      to="/news/notices"
      pending={notices.isPending}
      error={notices.error}
      empty={notices.data?.length === 0}
      emptyText="등록된 공지사항이 없습니다."
    >
      <div className="space-y-0.5">
        {notices.data?.map((post) => (
          <WidgetRow
            key={post.id}
            // 제목을 눌렀으면 그 글이다 — 목록으로 보내면 방금 읽은 제목을 다시 찾아야 한다.
            to={`/news/notices?post=${post.id}`}
            leading={
              <span className="badge badge-gray flex-shrink-0 badge-sm">
                {noticeCategoryLabel(post.category)}
              </span>
            }
            title={post.title}
            meta={relativeDay(post.createdAt)}
          />
        ))}
      </div>
    </WidgetFrame>
  );
}
